// ============ ASSETS: all artwork, base64-embedded (per brief) ============
const ASSETS = window.PLASMA_RUN_CONFIG.assets;
/* audio data in window.PLASMA_RUN_AUDIO, loaded async (see _loadSounds) */
// PLASMA RUN — all player-visible strings (zero UI literals in game code)
const STR = {
  title: "PLASMA RUN",
  subtitle: "Harvest the plasma. Outrun the Corp.",
  tapToStart: "Tap / Press any key to run",
  controlsDesktop: "← → or A D — switch lanes · ↑ / Space — jump",
  controlsTouch: "Swipe left / right — switch lanes · Swipe up — jump",
  score: "SCORE",
  best: "BEST",
  orbs: "ORBS",
  shieldReady: "SHIELD CHARGED",
  gameOver: "RUN TERMINATED",
  win: "EARLY ACCESS UNLOCKED",
  winSub: "You outran the Corp.",
  gateAhead: "ALL PLASMA COLLECTED — GATE AHEAD!",
  time: "TIME",
  goal: "PLASMA CHARGE",
  visitSite: "VISIT PUFFCO.COM \u2192",
  finalScore: "Score",
  newBest: "NEW BEST!",
  retry: "Tap / Press any key to retry",
  distance: "m",
};

// PLASMA RUN — sim.js: pure deterministic simulation (no DOM, no timers, no Math.random)
// Consumed by index.html (browser) and tools/route_test.mjs (node smoke).
// Determinism: all gameplay randomness flows from one seeded mulberry32 stream.

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- Tunables (thresholds.md is the source of truth) ----
const CFG = {
  lanes: [-1, 0, 1],
  baseSpeed: 6,            // world units / s
  maxSpeedMult: 2.15,      // v3: split the difference (orig 2.4 / marketing 1.9)
  rampTime: 75,            // s to reach max speed
  spawnZ: 30,              // spawn-to-player >= 30/(6*2.4) = 2.08 s at max speed (>= 1.9 threshold)
  playerZ: 1,
  collideHalf: 0.55,       // world-units half-window for collision at player plane
  orbCollectHalf: 0.8,
  jumpDur: 0.62,           // s airborne
  slideDur: 0.70,          // s sliding (passes under overhead beams)
  jumpClearLow: true,
  laneTween: 0.12,         // s visual tween; logical lane switches instantly
  inputBuffer: 0.12,       // s (>= 120 ms threshold)
  telegraphLead: 0.95,     // s before impact that Vexor flashes (>= 0.6 threshold incl. shift at 0.35)
  shiftLead: 0.35,         // s before impact that Vexor commits its lane shift
  orbsPerShield: 15,        // one shield mid-run — forgiveness for first-timers
  maxShield: 1,
  invulnTime: 1.5,
  gapBase: 10.6,            // world units between chunk anchors at base speed
  gapMin: 8.5,             // floor — 8.5/14.4 = 0.59 s between chunk anchors at max speed (>= 0.55 threshold)
  orbScore: 10,
  distScorePerUnit: 1,
  orbGoal: 42,           // collect ALL the plasma (quota) -> finish gate spawns
  finishLeadZ: 12,       // gate spawns this far beyond normal spawn depth
  winTimePar: 180,       // s; finishing faster earns (par - t) * winTimeRate bonus
  winTimeRate: 5,
  winFlatBonus: 500,
};

// ---- Chunk library: every chunk guarantees >= 1 clear path by construction ----
// e: type ('low'|'full'|'vexor'|'orb'), lane, dz (offset from chunk anchor), elevated (orbs)
const CHUNKS = [
  { id: 'breather', minT: 0, w: 1, e: [] },
  { id: 'orbLine', minT: 0, w: 2, e: (l) => [0, 1, 2, 3, 4].map(i => ({ t: 'orb', lane: l, dz: i * 1.1 })) },
  { id: 'orbRiver', minT: 6, w: 2, e: (l) => [0, 1, 2, 3, 4, 5, 6].map(i => ({ t: 'orb', lane: l, dz: i * 1.0 })) },
  { id: 'lowJump', minT: 0, w: 2, e: (l) => [{ t: 'low', lane: l, dz: 0 },
      { t: 'orb', lane: l, dz: -0.9, el: 1 }, { t: 'orb', lane: l, dz: 0, el: 1 }, { t: 'orb', lane: l, dz: 0.9, el: 1 }] },
  { id: 'fullDodge', minT: 4, w: 1, e: (l) => [{ t: 'full', lane: l, dz: 0 }, { t: 'orb', lane: sideOf(l), dz: 0 }] },
  { id: 'doubleWall', minT: 20, w: 1, e: (l) => [{ t: 'full', lane: l, dz: 0 }, { t: 'full', lane: otherOf(l), dz: 0 },
      { t: 'orb', lane: thirdOf(l), dz: -1 }, { t: 'orb', lane: thirdOf(l), dz: 0 }, { t: 'orb', lane: thirdOf(l), dz: 1 }] },
  { id: 'tripleLow', minT: 16, w: 1, e: () => [{ t: 'low', lane: -1, dz: 0 }, { t: 'low', lane: 0, dz: 0 }, { t: 'low', lane: 1, dz: 0 },
      { t: 'orb', lane: 0, dz: -0.9, el: 1 }, { t: 'orb', lane: 0, dz: 0, el: 1 }, { t: 'orb', lane: 0, dz: 0.9, el: 1 }] },
  { id: 'lowPair', minT: 34, w: 1, e: (l) => [{ t: 'low', lane: l, dz: 0 }, { t: 'low', lane: l, dz: 7.5 }] },
  { id: 'vexor', minT: 12, w: 2, e: (l, r) => [{ t: 'vexor', lane: l, dz: 0, to: clampLane(l + (r < 0.5 ? -1 : 1)) }] },
  { id: 'vexorBait', minT: 48, w: 1, e: (l, r) => {
      const to = clampLane(l + (r < 0.5 ? -1 : 1));
      return [{ t: 'vexor', lane: l, dz: 0, to }, { t: 'orb', lane: to, dz: -2.2 }, { t: 'orb', lane: to, dz: -1.4 }];
    } },
  { id: 'zigOrbs', minT: 8, w: 2, e: () => [-1, 0, 1, 0, -1].map((l, i) => ({ t: 'orb', lane: l, dz: i * 1.2 })) },
  { id: 'pitJump', minT: 8, w: 1, e: (l) => [{ t: 'pit', lane: l, dz: 0 },
      { t: 'orb', lane: l, dz: -0.9, el: 1 }, { t: 'orb', lane: l, dz: 0, el: 1 }, { t: 'orb', lane: l, dz: 0.9, el: 1 }] },
  { id: 'overBeam', minT: 12, w: 1, e: (l) => [{ t: 'over', lane: l, dz: 0 },
      { t: 'orb', lane: l, dz: -0.8 }, { t: 'orb', lane: l, dz: 0 }, { t: 'orb', lane: l, dz: 0.8 }] },
  { id: 'pitPair', minT: 46, w: 1, e: (l) => [{ t: 'pit', lane: l, dz: 0 }, { t: 'pit', lane: otherOf(l), dz: 0 },
      { t: 'orb', lane: thirdOf(l), dz: 0 }] },
  { id: 'beamWall', minT: 50, w: 1, e: (l) => [{ t: 'over', lane: l, dz: 0 }, { t: 'full', lane: otherOf(l), dz: 0 },
      { t: 'orb', lane: l, dz: 0 }] },
  { id: 'drone', minT: 18, w: 2, e: (l) => [{ t: 'drone', lane: l, dz: 0 },
      { t: 'orb', lane: sideOf(l), dz: -0.6 }, { t: 'orb', lane: sideOf(l), dz: 0.6 }] },
];
function sideOf(l) { return l === 0 ? 1 : 0; }
function otherOf(l) { return l === 0 ? 1 : (l === 1 ? -1 : 0); }
function thirdOf(l) { return [-1, 0, 1].find(x => x !== l && x !== otherOf(l)); }
function clampLane(l) { return Math.max(-1, Math.min(1, l)); }

function createGame(seed) {
  return {
    phase: 'title',          // 'title' | 'run' | 'over'
    seed,
    rng: mulberry32(seed),   // spawn/gameplay stream ONLY (FX uses its own stream client-side)
    t: 0, dist: 0, score: 0,
    lane: 0, laneVis: 0,
    jumpT: -1,               // -1 grounded, else seconds into jump
    slideT: -1,              // -1 upright, else seconds into slide
    orbs: 0, totalOrbs: 0, shield: 0, invuln: 0,
    combo: 0, comboT: 0, mult: 1, bestCombo: 0, relief: 0, justCombo: 0,
    speed: CFG.baseSpeed,
    ents: [],                // {t,lane,z,el?,to?,phase?(vexor:0 idle,1 flash,2 shifted),dead?}
    nextChunkZ: CFG.spawnZ * 0.38,
    buf: { left: 0, right: 0, jump: 0 }, // input buffer timers
    goalMet: 0, finishSpawned: 0, winT: 0,
    hitFlash: 0, justCollected: 0, justJumped: 0, justCrashed: 0, justShield: 0, justTelegraph: 0,
    justGoal: 0, justWon: 0, justSlid: 0,
  };
}

function start(g) {
  g.phase = 'run';
  // welcome plasma — a flowing, lane-weaving river from right in front of the player back into
  // the spawn horizon, so the opening is continuous and non-linear (no straight line, no dead zone).
  const weave = [0, 0, 1, 1, 0, -1, -1, 0, 1, 0, -1, 0, 1, 1, 0, -1, 0, 0, 1, 0];
  for (let i = 0; i < weave.length; i++) {
    g.ents.push({ t: 'orb', lane: weave[i], z: CFG.playerZ + CFG.baseSpeed + i * 1.05, dead: 0 });
  }
}

// cmds: {left,right,jump} — EDGE events this tick (booleans)
function step(g, dt, cmds) {
  // decay one-frame event flags
  g.justCollected = 0; g.justJumped = 0; g.justCrashed = 0; g.justShield = 0; g.justTelegraph = 0; g.justGoal = 0; g.justWon = 0; g.justSlid = 0;
  g.justCombo = 0; g.justShieldBreak = 0;
  if (g.phase !== 'run') return g;
  // combo decays if you go too long without a pickup
  if (g.comboT > 0) { g.comboT -= dt; if (g.comboT <= 0) { g.combo = 0; g.mult = 1; } }

  g.t += dt;
  const ramp = Math.min(1, g.t / CFG.rampTime);
  const cellMult = 1 + 0.05 * Math.floor(g.totalOrbs / 10);   // +5% forward velocity per 10 cells collected
  const timeMult = 1 + (CFG.maxSpeedMult - 1) * ramp;
  g.speed = CFG.baseSpeed * Math.min(CFG.maxSpeedMult, timeMult * cellMult);   // capped at our tuned solvability ceiling
  g.dist += g.speed * dt;
  g.score += g.speed * dt * CFG.distScorePerUnit;
  if (g.invuln > 0) g.invuln = Math.max(0, g.invuln - dt);

  // ---- input buffer (>=120ms forgiveness) ----
  for (const k of ['left', 'right', 'jump', 'down']) {
    if (cmds && cmds[k]) g.buf[k] = CFG.inputBuffer;
    else if (g.buf[k] > 0) g.buf[k] -= dt;
  }
  if (g.buf.left > 0)  { const nl = clampLane(g.lane - 1); if (nl !== g.lane) { g.lane = nl; } g.buf.left = 0; }
  if (g.buf.right > 0) { const nl = clampLane(g.lane + 1); if (nl !== g.lane) { g.lane = nl; } g.buf.right = 0; }
  if (g.buf.jump > 0 && g.jumpT < 0 && g.slideT < 0) { g.jumpT = 0; g.buf.jump = 0; g.justJumped = 1; }
  if (g.buf.down > 0 && g.slideT < 0 && g.jumpT < 0) { g.slideT = 0; g.buf.down = 0; g.justSlid = 1; }

  // visual lane tween + jump clock
  const dl = g.lane - g.laneVis;
  const maxStep = dt / CFG.laneTween;
  g.laneVis += Math.abs(dl) <= maxStep ? dl : Math.sign(dl) * maxStep;
  if (g.jumpT >= 0) { g.jumpT += dt; if (g.jumpT >= CFG.jumpDur) g.jumpT = -1; }
  if (g.slideT >= 0) { g.slideT += dt; if (g.slideT >= CFG.slideDur) g.slideT = -1; }

  // ---- spawn chunks ----
  // quota check -> spawn the finish gate, stop new chunks
  if (!g.goalMet && g.totalOrbs >= CFG.orbGoal) {
    g.goalMet = 1; g.justGoal = 1;
  }
  if (g.goalMet && !g.finishSpawned) {
    g.ents.push({ t: 'finish', lane: 0, z: CFG.spawnZ + CFG.finishLeadZ, dead: 0 });
    g.finishSpawned = 1;
  }
  if (!g.goalMet) {
    const late = Math.min(1, g.t / 75);
    const gap = Math.max(CFG.gapMin, (CFG.gapBase - 3.5 * ramp) * (1 - 0.13 * late) * (1 + (g.relief || 0)));
    g.nextChunkZ -= g.speed * dt;
    if (g.nextChunkZ <= 0) {
      spawnChunk(g);
      g.nextChunkZ += gap;
    }
  }

  // ---- advance entities ----
  const airborne = g.jumpT >= 0 && g.jumpT < CFG.jumpDur;
  const jumpPeakFrac = airborne ? Math.sin((g.jumpT / CFG.jumpDur) * Math.PI) : 0;
  for (const e of g.ents) {
    e.z -= g.speed * dt;
    if (e.t === 'vexor' && !e.dead) {
      const tti = (e.z - CFG.playerZ) / g.speed; // time to impact
      if (e.phase === 0 && tti <= CFG.telegraphLead) { e.phase = 1; g.justTelegraph = 1; }
      if (e.phase === 1 && tti <= CFG.shiftLead) { e.phase = 2; e.dir = Math.sign(e.to - e.lane); e.lane = e.to; }
    }
    if (e.dead) continue;
    const dzP = Math.abs(e.z - CFG.playerZ);
    if (e.t === 'finish') {
      if (e.z <= CFG.playerZ) {
        e.dead = 1; g.phase = 'win'; g.winT = g.t; g.justWon = 1;
        g.score += CFG.winFlatBonus + Math.max(0, (CFG.winTimePar - g.t)) * CFG.winTimeRate;
        return g;
      }
      continue;
    }
    if (e.t === 'orb') {
      if (dzP < CFG.orbCollectHalf && e.lane === g.lane) {
        const wantAir = !!e.el;
        if (wantAir === (jumpPeakFrac > 0.35) || (!wantAir && !airborne) || (!wantAir && airborne && jumpPeakFrac <= 0.35)) {
          e.dead = 1; g.orbs += 1; g.totalOrbs += 1; g.justCollected = 1;
          g.combo += 1; g.comboT = 2.6;
          const m = Math.min(5, 1 + Math.floor(g.combo / 5));
          if (m > g.mult) g.justCombo = 1;
          g.mult = m;
          if (g.combo > g.bestCombo) g.bestCombo = g.combo;
          g.score += CFG.orbScore * g.mult;
          if (g.orbs >= CFG.orbsPerShield && g.shield < CFG.maxShield) { g.orbs = 0; g.shield += 1; g.justShield = 1; }
        }
      }
    } else if (dzP < CFG.collideHalf && e.lane === g.lane) {
      const sliding = g.slideT >= 0;
      const cleared = ((e.t === 'low' || e.t === 'pit') && airborne && jumpPeakFrac > 0.25)
                   || ((e.t === 'over' || e.t === 'drone') && sliding);
      if (!cleared && g.invuln <= 0) {
        e.dead = 1;
        if (g.shield > 0) { g.shield -= 1; g.invuln = CFG.invulnTime; g.hitFlash = 1; g.justCrashed = 1; g.justShieldBreak = 1; g.combo = 0; g.mult = 1; }
        else { g.phase = 'over'; g.justCrashed = 1; return g; }
      }
    }
  }
  g.ents = g.ents.filter(e => e.z > -2 && !(e.dead && e.t === 'orb'));
  return g;
}

function spawnChunk(g) {
  const r = g.rng;
  let avail = CHUNKS.filter(c => g.t >= c.minT);
  // guaranteed mid-game robot beat: introduce the enforcer once we reach the
  // mid-game, even if the weighted roll never lands on it
  if (!g.vexorSpawned && g.t >= 13) {
    const vex = CHUNKS.find(c => c.id === 'vexor');
    if (vex) avail = [vex];
  }
  // then the aerial drone as escalation, guaranteed once the robot has been seen
  if (g.vexorSpawned && !g.droneSpawned && g.t >= 24) {
    const drC = CHUNKS.find(c => c.id === 'drone');
    if (drC) avail = [drC];
  }
  // weighted pick (orb chunks are heavier -> Subway-Surfers collect density)
  const totalW = avail.reduce((a, c) => a + (c.w || 1), 0);
  let roll = r() * totalW, pick = avail[0];
  for (const c of avail) { roll -= (c.w || 1); if (roll <= 0) { pick = c; break; } }
  if (pick.id === 'breather' && g.t > 8 && r() < 0.65) pick = avail[1 + Math.floor(r() * (avail.length - 1))];
  const lane = CFG.lanes[Math.floor(r() * 3)];
  const extra = r();
  const defs = typeof pick.e === 'function' ? pick.e(lane, extra) : pick.e;
  for (const d of defs) {
    g.spawnN = (g.spawnN || 0) + 1; // cosmetic variant counter (deterministic, outside the RNG stream)
    g.ents.push({ t: d.t, lane: typeof d.lane === 'number' ? clampLane(d.lane) : lane,
      z: CFG.spawnZ + (d.dz || 0), el: d.el || 0, to: d.to, phase: d.t === 'vexor' ? 0 : undefined, dead: 0,
      v: g.spawnN & 1 });
    if (d.t === 'vexor') g.vexorSpawned = 1;
    if (d.t === 'drone') g.droneSpawned = 1;
  }
}

// Reference route helper for smoke: a perfect-information policy that picks the
// safest lane each tick. NOT a "smart bot" — it is the recorded-route generator
// used once at authoring time; its output command list is the reference route.
function safePolicy(g) {
  // per-lane nearest hard-threat time; lows tracked separately (jumpable)
  const danger = { '-1': Infinity, '0': Infinity, '1': Infinity };
  const lowAt = { '-1': Infinity, '0': Infinity, '1': Infinity };   // jump-overs: lows + pits
  const overAt = { '-1': Infinity, '0': Infinity, '1': Infinity };  // slide-unders: beams
  for (const e of g.ents) {
    if (e.dead || e.t === 'orb' || e.t === 'finish') continue;
    const tti = (e.z - CFG.playerZ) / g.speed;
    // a hazard is live until it has fully cleared the collision window behind the player
    if (e.z < CFG.playerZ - CFG.collideHalf - 0.1 || tti > 1.6) continue;
    const eff = Math.max(0, tti);
    if (e.t === 'low' || e.t === 'pit') {
      lowAt[String(e.lane)] = Math.min(lowAt[String(e.lane)], eff);
    } else if (e.t === 'over' || e.t === 'drone') {
      overAt[String(e.lane)] = Math.min(overAt[String(e.lane)], eff);
    } else if (e.t === 'vexor') {
      // before the committed shift, the threatened lane is the destination;
      // after it, the actual lane. Cover both within the shift window.
      const lanes = e.phase === 2 ? [e.lane] : [e.to, e.lane];
      for (const L of lanes) danger[String(L)] = Math.min(danger[String(L)], eff);
    } else {
      danger[String(e.lane)] = Math.min(danger[String(e.lane)], eff);
    }
  }
  const airborne = g.jumpT >= 0;
  const airLeft = airborne ? (CFG.jumpDur - g.jumpT) : 0;
  const sliding = g.slideT >= 0;
  const slideLeft = sliding ? (CFG.slideDur - g.slideT) : 0;
  // a low/pit we cannot jump over in time, or a beam we cannot slide under in time, is a hard danger
  for (const L of ['-1', '0', '1']) {
    const lt = lowAt[L];
    if (lt !== Infinity) {
      const canJumpIt = (!airborne && !sliding && lt > 0.18) || (airborne && lt > airLeft + 0.12)
                     || (sliding && lt > slideLeft + 0.18);
      if (!canJumpIt) danger[L] = Math.min(danger[L], lt);
    }
    const ot = overAt[L];
    if (ot !== Infinity) {
      const canSlideIt = (!airborne && !sliding && ot > 0.12) || (sliding && ot < slideLeft - 0.05)
                      || (sliding && ot > slideLeft + 0.12) || (airborne && ot > airLeft + 0.12);
      if (!canSlideIt) danger[L] = Math.min(danger[L], ot);
    }
  }
  const cmds = { left: false, right: false, jump: false, down: false };
  const CROSS = 0.16; // s budget per lane step
  // effective safety of a lane = nearest hard danger, discounted by travel time
  function laneScore(target) {
    const steps = Math.abs(target - g.lane);
    const arrive = steps * CROSS;
    let worst = Infinity;
    // crossing lanes: every lane on the path must not hit us while passing through
    const path = [];
    for (let l = g.lane; l !== target; l += Math.sign(target - g.lane)) path.push(l + Math.sign(target - g.lane));
    path.forEach((l, i) => {
      const d = danger[String(l)];
      const enter = i * CROSS, leave = (i + 1) * CROSS + (l === target ? 1.0 : 0);
      if (d > enter - 0.05 && d < (l === target ? Infinity : leave)) worst = Math.min(worst, d - enter);
    });
    const dTarget = danger[String(target)];
    return Math.min(worst, dTarget - arrive);
  }
  const scores = { '-1': laneScore(-1), '0': laneScore(0), '1': laneScore(1) };
  const cur = scores[String(g.lane)];
  // orb attraction: when no threat is near, drift one lane toward the closest reachable orb
  if (cur > 1.2) {
    let bestOrb = null;
    for (const e of g.ents) {
      if (e.dead || e.t !== 'orb' || e.el) continue;
      const tti = (e.z - CFG.playerZ) / g.speed;
      if (tti < 0.35 || tti > 1.6) continue;
      if (bestOrb === null || tti < bestOrb.tti) bestOrb = { lane: e.lane, tti };
    }
    if (bestOrb && bestOrb.lane !== g.lane) {
      const stepL = g.lane + Math.sign(bestOrb.lane - g.lane);
      if (scores[String(stepL)] > 1.2) {
        if (stepL < g.lane) cmds.left = true; else cmds.right = true;
        return cmds;
      }
    }
  }
  // jump a low/pit in the current lane when it's imminent and we're grounded+upright
  const myLow = lowAt[String(g.lane)];
  if (myLow < 0.30 && myLow > 0 && !airborne && !sliding) cmds.jump = true;
  // slide under an imminent beam in the current lane
  const myOver = overAt[String(g.lane)];
  if (myOver < 0.30 && myOver > 0 && !airborne && !sliding) { cmds.down = true; cmds.jump = false; }
  if (cur < 0.7) {
    let bestLane = g.lane, bestScore = cur;
    for (const L of [-1, 0, 1]) {
      if (scores[String(L)] > bestScore + 0.08) { bestScore = scores[String(L)]; bestLane = L; }
    }
    if (bestLane !== g.lane) {
      if (bestLane < g.lane) cmds.left = true; else cmds.right = true;
      cmds.jump = false; // don't waste the jump mid-dodge
    }
  }
  return cmds;
}


/* STYLE FORMULA (governs all procedural art in this file, byte-identical):
Cinematic semi-photorealistic digital painting with heavy neon bloom and rain-slick reflections; hard-edged industrial silhouettes with no outlines, forms read by violet rim-light; environment in near-black indigo and charcoal under a stormy purple sky with ember-orange accents, hero in matte black techwear with deep purple hood contrasting the gloom, pickups and hazards marked with intense electric violet plasma glow; oppressive dystopian atmosphere lit only by plasma light; high contrast between game elements and backgrounds, clean readable silhouettes, consistent eye-level perspective across all assets.
*/

// ============ canvas / DPR ============
const canvas = document.getElementById("pr-canvas"), ctx = canvas.getContext("2d");
const DPR_CAP = 2;                                  // v3: crisper sprites on phones
// mobile-first base metric: tracks phone width in portrait, screen height on desktop
const BASE = () => Math.min(W * 1.35, H * 0.62);
let W = 0, H = 0;
function resize() {
  const dpr = Math.min(devicePixelRatio || 1, DPR_CAP);
  W = innerWidth; H = innerHeight;
  canvas.width = W * dpr; canvas.height = H * dpr;
  canvas.style.width = W + "px"; canvas.style.height = H + "px";
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
addEventListener("resize", resize); addEventListener("orientationchange", resize); resize();

// ============ assets ============
const IMGS = {};
const IMG_FILES = { bg: "bg.jpg", track: "track.jpg", nox: "nox.png",
  noxJump: "nox_jump.png", noxJump2: "nox_jump2.png", noxStrafe: "nox_strafe.png",
  noxLand: "nox_land.png", noxSlide: "nox_slide.png", noxSlide2: "nox_slide2.png",
  noxHit: "nox_hit.png", noxHit2: "nox_hit2.png", noxWin: "nox_win.png",
  vexor: "vexor.png", vexorL: "vexor_l.png", vexorR: "vexor_r.png",
  orb: "orb.png", low: "bar_low.png", full: "bar_full.png", beam: "beam.png",
  hole: "hole.png", finish: "finish.png", drone: "drone.png",
  cloud1: "cloud1.png", cloud2: "cloud2.png", cloud3: "cloud3.png", fog: "fog.png",
  pole_l: "pole_l.png", pole_r: "pole_r.png", logo: "logo.png" };
let assetsReady = false;
Promise.all(Object.entries(IMG_FILES).map(([k, f]) => new Promise((res) => {
  const im = new Image();
  im.decoding = "async";
  im.onload = () => { IMGS[k] = im; res(); };
  im.onerror = () => { res(); };  // optional slot — renderer falls back
  im.src = (typeof ASSETS !== "undefined" && ASSETS && ASSETS[f]) ? ASSETS[f] : "./assets/" + f;
}))).then(() => Promise.all([
  document.fonts.load('900 24px Orbitron'),
  document.fonts.load('700 24px Orbitron'),
  document.fonts.load('500 24px Orbitron')
]).catch(() => {})).then(() => { assetsReady = true; });

// ============ audio engine (v9) — generated SFX + looping music ============
let AC=null, sfxGain=null, musGain=null, masterGain=null, musSource=null, sndBuf={}, audioMuted=false;
try { audioMuted = localStorage.getItem("plasmaMuted")==="1"; } catch(e){}
function _startMusic(){
  if(!AC || !sndBuf.music || musSource) return;
  try { musSource=AC.createBufferSource(); musSource.buffer=sndBuf.music; musSource.loop=true; musSource.connect(musGain); musSource.start(0); } catch(e){}
}
function _loadSounds(){
  if(!AC) return;
  var S = window.PLASMA_RUN_AUDIO;
  if(!S || _loadSounds._done) return;
  _loadSounds._done = true;
  Object.keys(S).forEach(function(name){
    fetch(S[name]).then(function(r){return r.arrayBuffer();}).then(function(b){return AC.decodeAudioData(b);})
      .then(function(buf){ sndBuf[name]=buf; if(name==="music") _startMusic(); }).catch(function(){});
  });
}
window.__plasmaLoadSounds = _loadSounds;
function initAudio(){
  try { if(navigator.audioSession && !audioMuted) navigator.audioSession.type='playback'; } catch(e){}
  if(AC){ if(AC.state==="suspended") AC.resume(); return; }
  try { AC=new (window.AudioContext||window.webkitAudioContext)(); } catch(e){ AC=null; return; }
  masterGain=AC.createGain(); masterGain.gain.value=audioMuted?0:1; masterGain.connect(AC.destination);
  sfxGain=AC.createGain(); sfxGain.gain.value=0.9; sfxGain.connect(masterGain);
  musGain=AC.createGain(); musGain.gain.value=0.34; musGain.connect(masterGain);
  try{var _ub=AC.createBuffer(1,1,22050),_us=AC.createBufferSource();_us.buffer=_ub;_us.connect(AC.destination);_us.start(0);}catch(e){}
  _loadSounds();
  if(AC.state==="suspended") AC.resume();
}
var SFX_LVL={orb:0.62, jump:0.68, crash:0.85, slide:0.55, shield:0.8, win:0.95};
function sfx(name){
  if(!AC || audioMuted || !sndBuf[name]) return;
  try { var s=AC.createBufferSource(); s.buffer=sndBuf[name];
    var gg=AC.createGain(); gg.gain.value=SFX_LVL[name]||0.7; s.connect(gg); gg.connect(sfxGain); s.start(0);
  } catch(e){}
}
function toggleMute(){
  audioMuted=!audioMuted;
  if(AC && masterGain) masterGain.gain.setTargetAtTime(audioMuted?0:1, AC.currentTime, 0.015);
  try { localStorage.setItem("plasmaMuted", audioMuted?"1":"0"); } catch(e){}
  var mb=document.getElementById("muteBtn"); if(mb){ mb.classList.toggle("pr-muted", audioMuted); mb.setAttribute("aria-pressed", audioMuted?"true":"false"); }
  if(!AC && !audioMuted) initAudio();
}

// ============ input → command edges ============
const BIND = { ArrowLeft: "left", KeyA: "left", ArrowRight: "right", KeyD: "right",
  ArrowUp: "jump", KeyW: "jump", Space: "jump", ArrowDown: "down", KeyS: "down" };
let edges = { left: false, right: false, jump: false, down: false };
const WIN_LINK = window.PLASMA_RUN_CONFIG.winLink;
let anyKeyEdge = false;
addEventListener("keydown", (e) => {
  if (e.repeat) { e.preventDefault(); return; }
  anyKeyEdge = true; initAudio();
  const c = BIND[e.code]; if (c) { edges[c] = true; e.preventDefault(); }
});
// swipe (gameplay only — DOM overlays/buttons handle their own taps)
const onUI = (e) => e.target && e.target.closest && e.target.closest(".pr-screen, #pr-touchControls, #muteBtn");
let tStart = null;
addEventListener("touchstart", (e) => {
  initAudio();
  if (onUI(e) || g.phase !== "run") return;           // let DOM buttons behave natively
  const t = e.changedTouches[0];
  tStart = { x: t.clientX, y: t.clientY, t: performance.now() }; e.preventDefault();
}, { passive: false });
addEventListener("touchend", (e) => {
  if (onUI(e) || g.phase !== "run") { tStart = null; return; }
  if (!tStart) { e.preventDefault(); return; }
  const t = e.changedTouches[0], dx = t.clientX - tStart.x, dy = t.clientY - tStart.y;
  const adx = Math.abs(dx), ady = Math.abs(dy);
  if (Math.max(adx, ady) > 24) {
    if (adx > ady) edges[dx > 0 ? "right" : "left"] = true;
    else if (dy < 0) edges.jump = true;
    else edges.down = true;
  }
  tStart = null; e.preventDefault();
}, { passive: false });
addEventListener("pointerdown", initAudio);           // unlock audio on first interaction

// DOM controls (per brief: HTML menus + on-screen touch buttons)
const $ = (id) => document.getElementById(id);
const IS_TOUCH = "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
function newRun() {
  best = Math.max(best, Math.floor(g.score));
  g = createGame((Date.now() & 0xffffff) >>> 0);
  g.relief = Math.min(0.3, deaths * 0.1);   // soft assist: wider gaps after a death streak
  start(g);
}
$("startBtn").addEventListener("click", () => { initAudio(); if (g.phase === "title") start(g); });
$("retryBtn").addEventListener("click", () => { initAudio(); if (g.phase === "over") newRun(); });
$("againBtn").addEventListener("click", () => { initAudio(); if (g.phase === "win") newRun(); });
$("unlockBtn").href = WIN_LINK;
(function(){ var mb=$("muteBtn"); if(!mb) return;
  mb.classList.toggle("pr-muted", audioMuted); mb.setAttribute("aria-pressed", audioMuted?"true":"false");
  if (IS_TOUCH) mb.style.bottom="calc(env(safe-area-inset-bottom, 0px) + 88px)";
  mb.addEventListener("click", function(e){ e.stopPropagation(); toggleMute(); });
})();
$("mercyBtn").href = WIN_LINK;
let deaths = 0;                                       // session-scoped 3-fail mercy counter
{
  const lg = (typeof ASSETS !== "undefined" && ASSETS && ASSETS["logo.png"]) ? ASSETS["logo.png"] : "./assets/logo.png";
  $("logoImg").src = lg; $("logoImgWin").src = lg;
  { const pf = (typeof ASSETS!=="undefined" && ASSETS && ASSETS["pufflogo.png"]) ? ASSETS["pufflogo.png"] : "";
    if (pf) ["puffStart","puffWin","puffOver"].forEach(id => { const el=$(id); if(el) el.src = pf; }); }
}
$("howTo").textContent = IS_TOUCH
  ? "Swipe \u25C0 \u25B6 to switch lanes \u00B7 swipe \u25B2 to jump \u00B7 swipe \u25BC to slide"
  : "\u2190 \u2192 switch lanes \u00B7 \u2191 / space jump \u00B7 \u2193 slide";
for (const b of document.querySelectorAll("#pr-touchControls button")) {
  b.addEventListener("pointerdown", (e) => {
    e.preventDefault(); e.stopPropagation(); initAudio();
    if (g.phase === "run") edges[b.dataset.cmd] = true;
  });
}

// gamepad (standard mapping): dpad 12-15, south button 0, left stick axis 0
let padPrev = {};
function padEdges() {
  for (const gp of navigator.getGamepads?.() ?? []) {
    if (!gp) continue;
    const now = {
      left: gp.buttons[14]?.pressed || gp.axes[0] < -0.5,
      right: gp.buttons[15]?.pressed || gp.axes[0] > 0.5,
      jump: gp.buttons[0]?.pressed || gp.buttons[12]?.pressed,
      down: gp.buttons[13]?.pressed || gp.axes[1] > 0.5,
    };
    for (const k of ["left", "right", "jump", "down"]) {
      if (now[k] && !padPrev[k]) { edges[k] = true; anyKeyEdge = true; initAudio(); }
    }
    padPrev = now;
  }
}

// ============ game state ============
let g = createGame((Date.now() & 0xffffff) >>> 0);
const fxRng = mulberry32(0xC0FFEE); // visual-only stream — never touches gameplay
let best = 0;
try { best = +(localStorage.getItem("plasmaRunBest") || 0); } catch (e) {}
let shakeT = 0, slowmo = 0, overAt = 0;
let runHintT = 0, shieldMsgT = 0, lastPhase = null, comboMsgT = 0;
const vibe = (p) => { try { if (navigator.vibrate) navigator.vibrate(p); } catch (e) {} };
let particles = [];
let uiPhase = null;
let wasAirborne = false, landT = 0;
function syncUI() {
  // death/win beat: let the fall or victory pose play before the overlay drops in
  const HOLD = g.phase === "win" ? 1100 : 900;
  if ((g.phase === "over" || g.phase === "win") && performance.now() - overAt < HOLD) return;
  if (g.phase === uiPhase) return;
  uiPhase = g.phase;
  $("startScreen").classList.toggle("pr-hidden", g.phase !== "title");
  $("overScreen").classList.toggle("pr-hidden", g.phase !== "over");
  $("winScreen").classList.toggle("pr-hidden", g.phase !== "win");
  $("pr-touchControls").classList.toggle("pr-on", IS_TOUCH && g.phase === "run");
  if (g.phase === "win") {
    const t = `${Math.floor(g.winT / 60)}:${String(Math.floor(g.winT % 60)).padStart(2, "0")}`;
    $("winStats").textContent = `SCORE ${Math.floor(g.score)} \u00B7 TIME ${t}` +
      (g.score > best && best > 0 ? " \u00B7 NEW BEST!" : "");
  }
  if (g.phase === "over") {
    $("overStats").textContent = `SCORE ${Math.floor(g.score)} \u00B7 PLASMA ${g.totalOrbs}/${CFG.orbGoal}`;
    const mercy = deaths >= 1;
    $("overTitle").textContent = "RUN TERMINATED";
    $("mercyCopy").classList.toggle("pr-hidden-el", !mercy);
    $("mercyBtn").classList.toggle("pr-hidden-el", !mercy);
    $("retryBtn").textContent = mercy ? "RUN IT BACK" : "RETRY";
    $("retryBtn").classList.toggle("pr-primary", mercy);
  }
}
let collectPulse = 0;
function popAt() {  // small plasma pop at the player on collect (visual-only, fxRng)
  const pp = proj(g.laneVis, CFG.playerZ, 0);
  for (let i = 0; i < 7; i++) {
    const a = fxRng() * Math.PI * 2, sp = 60 + fxRng() * 160;
    particles.push({ x: pp.x, y: pp.y - BASE() * 0.16, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 60,
      r: 1.5 + fxRng() * 2.5, life: 0.55, c: "186,127,255" });
  }
}
function burst() {
  particles = [];
  for (let i = 0; i < 56; i++) {
    const a = fxRng() * Math.PI * 2, sp = 80 + fxRng() * 380;
    particles.push({ x: W / 2, y: H * 0.42, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 120,
      r: 2 + fxRng() * 5, life: 1, c: fxRng() < 0.7 ? "176,107,255" : "255,170,90" });
  }
}
function shieldShatter() {  // cyan shard burst when the shield breaks
  const pp = proj(g.laneVis, CFG.playerZ, 0);
  const cy = pp.y - BASE() * 0.18;
  for (let i = 0; i < 18; i++) {
    const a = fxRng() * Math.PI * 2, sp = 120 + fxRng() * 320;
    particles.push({ x: pp.x, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 80,
      r: 2 + fxRng() * 4, life: 0.7, c: fxRng() < 0.5 ? "150,225,255" : "210,245,255" });
  }
}
function stepParticles(dt) {
  for (const pt of particles) {
    pt.x += pt.vx * dt; pt.y += pt.vy * dt; pt.vy += 220 * dt; pt.life -= dt * 0.55;
  }
  particles = particles.filter(pt => pt.life > 0);
}

// ============ projection (vanishing-point pseudo-3D, per the approved wireframe) ============
function horizonY() { return H * 0.43; }
function proj(lane, z, el) {
  const f = CFG.playerZ / Math.max(z, CFG.playerZ * 0.6);     // 1 at player, →0 far
  const fMin = CFG.playerZ / CFG.spawnZ;
  const n = Math.max(0, (f - fMin) / (1 - fMin));             // normalized 0..1
  const ny = Math.pow(n, 1.25);
  const nearY = H * (IS_TOUCH ? 0.84 : 0.90);          // keep the runner clear of the touch bar
  const y = horizonY() + (nearY - horizonY()) * ny;
  const laneNear = Math.min(W * 0.33, BASE() * 0.36);
  const x = W / 2 + lane * laneNear * f;
  const s = f;                                                 // sprite scale factor
  const lift = el ? BASE() * 0.16 * f : 0;
  return { x, y: y - lift, s, n };
}

// ============ render ============
let drawCount = 0;
function glowPad(x, yBase, w, h, col) {  // additive plate behind hazards so they read on the dark road
  col = col || "168,100,255";
  ctx.save(); ctx.globalCompositeOperation = "lighter";
  const gl = ctx.createRadialGradient(x, yBase - h * 0.4, h * 0.08, x, yBase - h * 0.4, Math.max(w, h) * 0.75);
  gl.addColorStop(0, "rgba(" + col + ",0.30)"); gl.addColorStop(1, "rgba(" + col + ",0)");
  ctx.fillStyle = gl;
  ctx.fillRect(x - w, yBase - h * 1.6, w * 2, h * 2); drawCount++;
  ctx.restore();
}
function dr(img, x, y, w, h) { ctx.drawImage(img, x, y, w, h); drawCount++; }

function render(now) {
  drawCount = 0;
  ctx.clearRect(0, 0, W, H);
  const hy = horizonY();
  const speedN = (g.speed - CFG.baseSpeed) / (CFG.baseSpeed * (CFG.maxSpeedMult - 1));

  // camera shake
  ctx.save();
  if (shakeT > 0) {
    const a = shakeT * 10;
    ctx.translate((fxRng() - 0.5) * a, (fxRng() - 0.5) * a);
  }

  // --- sky / panorama (parallax drift + bob) ---
  if (IMGS.bg) {
    // cover-fit: fill from the top of frame down past the horizon, bottom-anchored at the horizon line
    const areaH = hy + H * 0.05, ar = IMGS.bg.width / IMGS.bg.height;
    let bh = areaH, bw = bh * ar;
    if (bw < W * 1.10) { bw = W * 1.10; bh = bw / ar; }
    // slow horizontal SWAY (sine) instead of a wrapping modulo — never snaps/resets.
    // amplitude (4% of bw) stays within the >=5% overhang so no screen edge is ever exposed.
    const ox = Math.sin(g.dist * 0.04) * (bw * 0.04) - (bw - W) / 2;
    const oy = Math.sin(g.dist * 0.35) * 2;
    dr(IMGS.bg, ox, areaH - bh + oy, bw, bh);
    ctx.fillStyle = "rgba(8,6,16,0.22)"; ctx.fillRect(0, 0, W, areaH + 2); drawCount++; // sit the sky back
  } else { ctx.fillStyle = "#120c22"; ctx.fillRect(0, 0, W, hy + 8); drawCount++; }

  // --- abyss sides: dark depth gradient + slow fog, no physical walls (v5 spec) ---
  const bottom = H, roadNearHalf = Math.min(W * 0.56, BASE() * 0.62), roadFarHalf = Math.min(W * 0.05, BASE() * 0.055);
  {
    const ab = ctx.createLinearGradient(0, hy, 0, bottom);
    ab.addColorStop(0, "#171029"); ab.addColorStop(0.35, "#0d0920"); ab.addColorStop(1, "#050310");
    ctx.fillStyle = ab; ctx.fillRect(0, hy, W, bottom - hy); drawCount++;
  }
  if (IMGS.fog) {
    // side fog banks: gentle vertical drift, mirrored pair, soft parallax — clipped to the abyss
    const fw = W * 0.62, fh = fw * (IMGS.fog.height / IMGS.fog.width);
    const SPACING = fh * 0.92;
    const drift = (g.dist * 3) % SPACING;              // period == spacing: wrap frame is pixel-identical
    ctx.save(); ctx.globalAlpha = 0.5;
    ctx.beginPath(); ctx.rect(0, hy - 4, W, H - hy + 4); ctx.clip();
    for (const side of [-1, 1]) {
      for (let k = -1; k <= 3; k++) {
        const fy = hy - fh * 0.25 + k * SPACING + drift - fh;
        if (fy > H || fy + fh < hy - fh * 0.3) continue;
        ctx.save();
        if (side === 1) { ctx.translate(W, 0); ctx.scale(-1, 1); }
        ctx.drawImage(IMGS.fog, -fw * 0.34, fy, fw, fh); drawCount++;
        ctx.restore();
      }
    }
    ctx.restore();
  }
  // horizon seam softener: a wide, low cloud bank straddling the horizon line — breaks the hard skyline/abyss seam (B2)
  if (IMGS.cloud1) {
    const seamImgs = [IMGS.cloud2 || IMGS.cloud1, IMGS.cloud1, IMGS.cloud3 || IMGS.cloud1, IMGS.cloud2 || IMGS.cloud1, IMGS.cloud1];
    ctx.save();
    ctx.beginPath(); ctx.rect(0, hy - 50, W, 132); ctx.clip();
    const sbw = W * 0.66, sbh = sbw * 0.32;
    for (let i = 0; i < seamImgs.length; i++) {
      const img = seamImgs[i]; if (!img) continue;
      const baseX = (i + 0.5) / seamImgs.length * W;
      const cx = baseX + Math.sin(g.dist * 0.05 + i * 1.7) * (W * 0.045);
      const cy = hy + 4 + Math.sin(g.dist * 0.08 + i * 2.1) * 4;
      ctx.globalAlpha = 0.34 + 0.06 * Math.sin(g.dist * 0.1 + i);
      ctx.drawImage(img, cx - sbw / 2, cy - sbh / 2, sbw, sbh); drawCount++;
    }
    ctx.restore();
  }
  // drifting cloud puffs floating in the abyss (slow, toward the player)
  if (IMGS.cloud1) {
    const clouds = [
      { img: IMGS.cloud1, side: -1, lane: 0.74, sc: 0.34, sp: 9, ph: 0.0 },
      { img: IMGS.cloud2, side: 1, lane: 0.78, sc: 0.30, sp: 7, ph: 0.45 },
      { img: IMGS.cloud3 || IMGS.cloud1, side: -1, lane: 0.88, sc: 0.24, sp: 5.5, ph: 0.78 },
      { img: IMGS.cloud2, side: 1, lane: 0.92, sc: 0.20, sp: 4.5, ph: 0.2 },
    ];
    ctx.save();
    ctx.beginPath(); ctx.rect(0, hy - 10, W, H - hy + 10); ctx.clip();
    for (const c of clouds) {
      if (!c.img) continue;
      const span = bottom - hy + 220;
      const cy = hy - 60 + (((g.dist * c.sp) + c.ph * span) % span);
      const n = Math.min(1, Math.max(0, (cy - hy) / (bottom - hy) + 0.12));
      const lt = (cy - (hy - 60)) / span;              // 0..1 position in the loop
      const edge = Math.min(1, Math.min(lt, 1 - lt) / 0.14);  // ease in/out at the wrap points
      const cw = W * c.sc * (0.55 + n * 0.9);
      const ch = cw * (c.img.height / c.img.width);
      const cx = W / 2 + c.side * (W * c.lane * (0.25 + n * 0.85));
      ctx.save(); ctx.globalAlpha = (0.30 + 0.35 * n) * edge;
      ctx.drawImage(c.img, cx - cw / 2, cy - ch / 2, cw, ch); drawCount++;
      ctx.restore();
    }
    ctx.restore();
  }

  // --- track: slice-mapped INSIDE a smooth clipped trapezoid (no stepped edges) ---
  if (IMGS.track) {
    const tex = IMGS.track, texH = tex.height;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(W / 2 - roadFarHalf, hy - 1); ctx.lineTo(W / 2 + roadFarHalf, hy - 1);
    ctx.lineTo(W / 2 + roadNearHalf, bottom + 2); ctx.lineTo(W / 2 - roadNearHalf, bottom + 2);
    ctx.closePath(); ctx.clip();
    const SLICES = 64;
    for (let i = 0; i < SLICES; i++) {
      const n0 = i / SLICES, n1 = (i + 1) / SLICES;
      const y0 = hy + (bottom - hy) * Math.pow(n0, 1.25);
      const y1 = hy + (bottom - hy) * Math.pow(n1, 1.25) + 1;
      const f = (n0 * (1 - CFG.playerZ / CFG.spawnZ)) + CFG.playerZ / CFG.spawnZ;
      const z = CFG.playerZ / f;
      const v = ((z + g.dist) * 0.5) % 1;
      const sy = (1 - v) * (texH - 2);
      const half = (roadFarHalf + (roadNearHalf - roadFarHalf) * Math.pow(n0, 1.18)) + 3; // overscan into the clip
      ctx.drawImage(tex, 0, sy, tex.width, Math.max(2, texH / SLICES),
        W / 2 - half, y0, half * 2, Math.max(1, y1 - y0));
      drawCount++;
    }
    ctx.restore();
    // crisp glowing road edges seal the texture against the ground
    ctx.save(); ctx.globalCompositeOperation = "lighter";
    for (const sgn of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(W / 2 + sgn * roadFarHalf, hy);
      ctx.lineTo(W / 2 + sgn * roadNearHalf, bottom);
      const gd = ctx.createLinearGradient(0, hy, 0, bottom);
      gd.addColorStop(0, "rgba(160,100,255,0.05)"); gd.addColorStop(1, "rgba(168,110,255,0.5)");
      ctx.strokeStyle = gd; ctx.lineWidth = 2.5; ctx.stroke(); drawCount++;
    }
    ctx.restore();
  }

  // lane divider glow strips (procedural, FORMULA: electric violet plasma glow)
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (const ld of [-0.5, 0.5]) {
    ctx.beginPath();
    const p0 = proj(ld * 2, CFG.spawnZ, 0), p1 = proj(ld * 2, CFG.playerZ, 0);
    const grad = ctx.createLinearGradient(p0.x, p0.y, p1.x, p1.y);
    grad.addColorStop(0, "rgba(150,90,255,0)");
    grad.addColorStop(1, "rgba(168,110,255,0.34)");
    ctx.strokeStyle = grad; ctx.lineWidth = 2 + 3 * speedN;
    ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.stroke(); drawCount++;
  }
  ctx.restore();

  // horizon fog (procedural gradient — manifest amendment, FORMULA embedded above)
  const fog = ctx.createLinearGradient(0, hy - H * 0.07, 0, hy + H * 0.18);
  fog.addColorStop(0, "rgba(36,20,66,0)");
  fog.addColorStop(0.45, "rgba(40,24,74,0.72)");
  fog.addColorStop(1, "rgba(12,8,24,0)");
  ctx.fillStyle = fog; ctx.fillRect(0, hy - H * 0.07, W, H * 0.28); drawCount++;

  // --- roadside pylons: detailed bridge-pole assets repeated along both road edges ---
  if (IMGS.pole_l && IMGS.pole_r) {
    const RAIL_INT = 2.6;                              // world units between pylons (decent frequency)
    const first = Math.ceil((g.dist + CFG.playerZ) / RAIL_INT);
    for (let k = 24; k >= 0; k--) {                    // far -> near (near overlaps far)
      const idx = first + k;
      const z = idx * RAIL_INT - g.dist;
      if (z < CFG.playerZ * 0.55 || z > CFG.spawnZ) continue;
      for (const side of [-1, 1]) {
        const img = side < 0 ? IMGS.pole_l : IMGS.pole_r;
        const p = proj(side * 1.62, z, 0);
        const a = Math.min(1, Math.max(0, p.n * 2.4));
        if (a <= 0) continue;
        const ph = BASE() * 0.62 * p.s;                 // pylon height
        const pw = ph * (img.width / img.height);
        ctx.save(); ctx.globalAlpha = a;
        // violet plasma glow where the pole's lit strip meets the catwalk
        ctx.globalCompositeOperation = "lighter";
        const ug = ctx.createRadialGradient(p.x, p.y, 0.5, p.x, p.y, Math.max(4, pw * 0.75));
        ug.addColorStop(0, `rgba(168,108,255,${0.5 * a})`); ug.addColorStop(1, "rgba(150,90,255,0)");
        ctx.fillStyle = ug; ctx.beginPath(); ctx.ellipse(p.x, p.y, pw * 0.75, pw * 0.30, 0, 0, 7); ctx.fill(); drawCount++;
        ctx.globalCompositeOperation = "source-over";
        dr(img, p.x - pw / 2, p.y - ph, pw, ph);
        ctx.restore();
      }
    }
  }

  // --- entities far → near ---
  const ents = [...g.ents].sort((a, b) => b.z - a.z);
  for (const e of ents) {
    if (e.dead && e.t !== "vexor") continue;
    const p = proj(e.lane, e.z, e.el);
    const base = BASE();
    // passed obstacles exit fast — no more looming at the bottom edge for a beat
    const behindZ = CFG.playerZ - e.z;
    const exitA = (e.t === "finish") ? 1 : (behindZ > 0 ? 1 - behindZ / 0.45 : 1);
    if (exitA <= 0) continue;
    ctx.globalAlpha = exitA;
    if (e.t === "pit") {
      if (IMGS.hole) {
        // the HOLE asset laid flat on the roadway (perspective squash)
        const pw = base * 0.62 * p.s, ph = pw * 0.40;
        ctx.save(); ctx.globalAlpha = Math.min(1, p.n * 3);
        // dark recess behind the art so it reads as a pit sunk into the road, not a floating decal
        ctx.fillStyle = "rgba(2,1,6,0.9)";
        ctx.beginPath(); ctx.ellipse(p.x, p.y - ph * 0.22, pw * 0.5, ph * 0.6, 0, 0, 7); ctx.fill(); drawCount++;
        dr(IMGS.hole, p.x - pw / 2, p.y - ph * 0.30, pw, ph);
        ctx.restore();
      } else {
        const pw = base * 0.56 * p.s, ph = pw * 0.30;
        ctx.fillStyle = "#030208";
        ctx.beginPath(); ctx.ellipse(p.x, p.y - ph * 0.4, pw / 2, ph / 2, 0, 0, 7); ctx.fill(); drawCount++;
      }
    } else if (e.t === "over") {
      if (IMGS.beam) {
        const gw = base * 0.62 * p.s;                  // gate spans its lane; pillars frame it
        const gh = gw * (IMGS.beam.height / IMGS.beam.width);
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        const gl = ctx.createRadialGradient(p.x, p.y - gh * 0.62, gh * 0.1, p.x, p.y - gh * 0.62, gh * 0.7);
        gl.addColorStop(0, "rgba(90,210,245,0.22)"); gl.addColorStop(1, "rgba(90,210,245,0)");
        ctx.fillStyle = gl; ctx.fillRect(p.x - gw, p.y - gh * 1.3, gw * 2, gh * 1.3); drawCount++;
        ctx.restore();
        dr(IMGS.beam, p.x - gw / 2, p.y - gh, gw, gh);   // bottom-anchored on the road
      } else {
        const bw = base * 0.78 * p.s, clearY = base * 0.19 * p.s;
        ctx.fillStyle = "#13101f"; ctx.fillRect(p.x - bw / 2, p.y - clearY - bw * 0.10, bw, bw * 0.10); drawCount++;
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = "rgba(168,110,255,0.8)";
        ctx.fillRect(p.x - bw / 2, p.y - clearY - bw * 0.015, bw, bw * 0.015); drawCount++;
        ctx.restore();
      }
    } else if (e.t === "orb" && IMGS.orb) {
      const s = base * 0.19 * p.s;
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      const pulse = 1 + 0.08 * Math.sin(now / 140 + e.z);
      dr(IMGS.orb, p.x - s * pulse / 2, p.y - s * pulse * 0.78, s * pulse, s * pulse);
      ctx.restore();
    } else if (e.t === "low" && IMGS.low) {
      const img = IMGS.low;
      const w = base * 0.50 * p.s, h = w * (img.height / img.width);
      glowPad(p.x, p.y, w, h * 0.9, "255,170,70");
      dr(img, p.x - w / 2, p.y - h, w, h);
    } else if (e.t === "full" && IMGS.full) {
      const img = IMGS.full;
      const h = base * 0.50 * p.s, w = h * (img.width / img.height);
      glowPad(p.x, p.y, w, h * 0.9);
      dr(img, p.x - w / 2, p.y - h, w, h);
    } else if (e.t === "finish" && !e.dead) {
      const gw = (roadFarHalf + (roadNearHalf - roadFarHalf) * Math.pow(p.n, 1.18)) * 2.3;
      if (IMGS.finish) {
        const gh = gw * (IMGS.finish.height / IMGS.finish.width);
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        dr(IMGS.finish, p.x - gw / 2, p.y - gh, gw, gh);
        ctx.restore();
      } else {
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = "rgba(176,107,255,0.5)"; ctx.fillRect(p.x - gw / 2, p.y - gw * 0.5, gw, gw * 0.06); drawCount++;
        ctx.fillRect(p.x - gw / 2, p.y - gw * 0.5, gw * 0.04, gw * 0.5); ctx.fillRect(p.x + gw / 2 - gw * 0.04, p.y - gw * 0.5, gw * 0.04, gw * 0.5); drawCount += 2;
        ctx.restore();
      }
    } else if (e.t === "vexor" && IMGS.vexor && !e.dead) {
      let vimg = IMGS.vexor;
      if (e.phase === 2 && e.dir) {                  // mid-shift: lunge sprite, fades back after the move
        const lunging = (e.z - CFG.playerZ) / Math.max(1, g.speed) > -0.1;
        if (lunging) vimg = (e.dir < 0 ? IMGS.vexorL : IMGS.vexorR) || IMGS.vexor;
      }
      const h = base * 0.52 * p.s, w = h * (vimg.width / vimg.height);
      if (e.phase === 1) { // telegraph: visor flash aura
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        const a = 0.35 + 0.3 * Math.sin(now / 60);
        ctx.fillStyle = `rgba(190,120,255,${a})`;
        ctx.beginPath(); ctx.ellipse(p.x, p.y - h * 0.82, w * 0.34, w * 0.2, 0, 0, 7); ctx.fill(); drawCount++;
        ctx.restore();
      }
      dr(vimg, p.x - w / 2, p.y - h, w, h);
    } else if (e.t === "drone" && IMGS.drone) {
      const img = IMGS.drone;
      const w = base * 0.50 * p.s, h = w * (img.height / img.width);
      const hover = base * 0.34 * p.s + Math.sin(now / 260 + e.z) * base * 0.02 * p.s; // hovers overhead — clear slide channel beneath
      glowPad(p.x, p.y - hover * 0.5, w * 0.8, h * 0.8, "90,210,245");    // slide-hazard = cyan
      dr(img, p.x - w / 2, p.y - hover - h, w, h);
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      // cyan "duck line" at the drone's underside — telegraphs the slide channel
      const ux = p.x, uy = p.y - hover;
      const lg = ctx.createLinearGradient(ux - w * 0.5, 0, ux + w * 0.5, 0);
      const lp = 0.45 + 0.3 * Math.sin(now / 120);
      lg.addColorStop(0, "rgba(90,210,245,0)"); lg.addColorStop(0.5, `rgba(130,228,252,${lp})`); lg.addColorStop(1, "rgba(90,210,245,0)");
      ctx.fillStyle = lg; ctx.fillRect(ux - w * 0.5, uy - Math.max(1, h * 0.02), w, Math.max(2, h * 0.04)); drawCount++;
      // menacing red sensor pulse
      const ep = 0.4 + 0.4 * Math.sin(now / 90);
      ctx.fillStyle = `rgba(255,60,60,${ep * 0.5})`;
      ctx.beginPath(); ctx.ellipse(p.x, p.y - hover - h * 0.42, w * 0.10, w * 0.10, 0, 0, 7); ctx.fill(); drawCount++;
      ctx.restore();
    }
  }

  ctx.globalAlpha = 1;
  // --- player (Nox) ---
  if (g.phase !== "title" && IMGS.nox) {
    const pp = proj(g.laneVis, CFG.playerZ, 0);
    const base = BASE();
    const airborne = g.jumpT >= 0, sliding = g.slideT >= 0;
    const strafeDir = g.lane - g.laneVis;            // nonzero while changing lanes
    // pose state machine (spec): run / jump asc+desc / airborne strafe / landing / slide x2 / hit x2 / win
    if (!airborne && wasAirborne) landT = 0.16;       // touch-down beat
    wasAirborne = airborne;
    if (landT > 0) landT -= 1 / 60;
    let sprite = IMGS.nox, hMul = 1, wMul = 1, mirror = false;
    const animF = Math.floor(now / 140) % 2;          // 2-frame alternation clock (~7Hz)
    if (g.phase === "over") {
      sprite = (animF ? IMGS.noxHit2 : IMGS.noxHit) || IMGS.noxHit || IMGS.nox; hMul = 0.92;
    } else if (g.phase === "win" && IMGS.noxWin) { sprite = IMGS.noxWin; }
    else if (airborne) {
      const ascending = g.jumpT < CFG.jumpDur * 0.45;
      if (Math.abs(strafeDir) > 0.18 && IMGS.noxStrafe) { sprite = IMGS.noxStrafe; mirror = strafeDir > 0; }
      else sprite = (ascending ? IMGS.noxJump : (IMGS.noxJump2 || IMGS.noxJump)) || IMGS.nox;
    }
    else if (sliding) { sprite = (animF ? IMGS.noxSlide2 : IMGS.noxSlide) || IMGS.noxSlide || IMGS.nox; if (!IMGS.noxSlide) { hMul = 0.55; wMul = 1.18; } }
    else if (landT > 0 && IMGS.noxLand) { sprite = IMGS.noxLand; hMul = 0.93; }
    else if (Math.abs(strafeDir) > 0.18 && IMGS.noxStrafe) { sprite = IMGS.noxStrafe; mirror = strafeDir > 0; }
    else { mirror = animF === 1; }                     // run cycle: stride alternation (mirrored beat)
    const h = base * 0.355 * hMul;                       // height-based: stable across aspect ratios
    const dw = h * (sprite.width / sprite.height) * wMul;
    const w = dw;
    const bob = (!airborne && !sliding) ? Math.abs(Math.sin(g.dist * 2.1)) * base * 0.012 : 0;
    const jump = airborne ? Math.sin((g.jumpT / CFG.jumpDur) * Math.PI) * base * 0.245 : 0;
    const lean = (g.lane - g.laneVis) * 0.35;            // Subway-Surfers lean into the lane change
    const blink = g.invuln > 0 && Math.floor(now / 90) % 2 === 0;
    if (!blink) {
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      const rim = ctx.createRadialGradient(pp.x, pp.y - h * 0.45 - jump, h * 0.1, pp.x, pp.y - h * 0.45 - jump, h * 0.62);
      rim.addColorStop(0, "rgba(150,90,255,0.20)"); rim.addColorStop(1, "rgba(150,90,255,0)");
      ctx.fillStyle = rim; ctx.fillRect(pp.x - h * 0.7, pp.y - h * 1.1 - jump, h * 1.4, h * 1.3); drawCount++;
      ctx.restore();
      // contact shadow
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.beginPath(); ctx.ellipse(pp.x, pp.y - 2, dw * 0.46 * (1 - jump / (base * 0.3)), dw * 0.12, 0, 0, 7);
      ctx.fill(); drawCount++;
      if (mirror || Math.abs(lean) > 0.01) {
        ctx.save(); ctx.translate(pp.x, pp.y - jump); ctx.rotate(lean);
        if (mirror) ctx.scale(-1, 1);
        ctx.drawImage(sprite, -dw / 2, -h - bob, dw, h); drawCount++;
        ctx.restore();
      } else dr(sprite, pp.x - dw / 2, pp.y - h - bob - jump, dw, h);
      if (g.shield > 0) {
        const cxp = pp.x, cyp = pp.y - h * 0.5 - bob - jump, rr = h * 0.57, ry = h * 0.64;
        const pulse = 0.5 + 0.5 * Math.sin(now / 220);
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        const sb = ctx.createRadialGradient(cxp, cyp, rr * 0.22, cxp, cyp, rr * 1.05);
        sb.addColorStop(0, "rgba(120,200,255,0)");
        sb.addColorStop(0.78, `rgba(120,190,255,${0.05 + 0.06 * pulse})`);
        sb.addColorStop(1, `rgba(150,205,255,${0.26 + 0.16 * pulse})`);
        ctx.fillStyle = sb;
        ctx.beginPath(); ctx.ellipse(cxp, cyp, rr, ry, 0, 0, 7); ctx.fill(); drawCount++;
        ctx.lineWidth = 2; ctx.strokeStyle = `rgba(160,215,255,${0.30 + 0.28 * pulse})`;
        ctx.beginPath(); ctx.ellipse(cxp, cyp, rr, ry, 0, 0, 7); ctx.stroke(); drawCount++;
        ctx.lineWidth = 3; ctx.strokeStyle = "rgba(225,248,255,0.9)";
        const rot = now / 600;
        for (let k = 0; k < 2; k++) { const a0 = rot + k * Math.PI;
          ctx.beginPath(); ctx.ellipse(cxp, cyp, rr, ry, 0, a0, a0 + 0.7); ctx.stroke(); drawCount++; }
        ctx.restore();
      }
    }
  }

  // speed-line vignette at high speed (procedural)
  if (speedN > 0.30) {
    ctx.save(); ctx.globalCompositeOperation = "lighter";
    ctx.strokeStyle = `rgba(140,90,255,${0.08 * speedN})`; ctx.lineWidth = 1.5;
    for (let i = 0; i < 6; i++) {
      const a = fxRng() * Math.PI * 2, r0 = BASE() * 0.46, r1 = r0 + 60 + fxRng() * 80;
      ctx.beginPath();
      ctx.moveTo(W / 2 + Math.cos(a) * r0, H / 2 + Math.sin(a) * r0);
      ctx.lineTo(W / 2 + Math.cos(a) * r1, H / 2 + Math.sin(a) * r1);
      ctx.stroke(); drawCount++;
    }
    ctx.restore();
  }

  // win celebration: plasma burst particles (visual-only, fxRng)
  if (particles.length) {
    ctx.save(); ctx.globalCompositeOperation = "lighter";
    for (const pt of particles) {
      ctx.fillStyle = `rgba(${pt.c},${Math.max(0, pt.life)})`;
      ctx.beginPath(); ctx.arc(pt.x, pt.y, pt.r * Math.max(0.2, pt.life), 0, 7); ctx.fill(); drawCount++;
    }
    ctx.restore();
  }

  // rain streaks (visual-only, fxRng) — matches the reference's storm
  ctx.save(); ctx.globalCompositeOperation = "lighter";
  ctx.strokeStyle = "rgba(170,150,230,0.10)"; ctx.lineWidth = 1;
  for (let i = 0; i < 22; i++) {
    const rx = fxRng() * W, ry = fxRng() * H, len = 14 + fxRng() * 22;
    ctx.beginPath(); ctx.moveTo(rx, ry); ctx.lineTo(rx - len * 0.18, ry + len); ctx.stroke(); drawCount++;
  }
  ctx.restore();

  ctx.restore(); // shake

  // vignette frames the action like a real runner
  const vg = ctx.createRadialGradient(W / 2, H * 0.55, BASE() * 0.45, W / 2, H * 0.55, Math.max(W, H) * 0.78);
  vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(4,2,10,0.55)");
  ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H); drawCount++;

  renderHUD(now);
}

function text(str, x, y, size, color, align = "center", weight = 700, spacing = 0) {
  ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = "middle";
  ctx.font = `${weight} ${size}px "Orbitron", system-ui, sans-serif`;
  try { ctx.letterSpacing = spacing + "px"; } catch (e) {}
  ctx.fillText(str, x, y); drawCount++;
  try { ctx.letterSpacing = "0px"; } catch (e) {}
}

function roundRectP(x, y, w, h, r) {
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
  else { ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); }
}

function hudPanel(x, y, w, h) {
  const c = Math.min(15, h * 0.24);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + c, y); ctx.lineTo(x + w - c, y); ctx.lineTo(x + w, y + c);
  ctx.lineTo(x + w, y + h - c); ctx.lineTo(x + w - c, y + h); ctx.lineTo(x + c, y + h);
  ctx.lineTo(x, y + h - c); ctx.lineTo(x, y + c); ctx.closePath();
  const g2 = ctx.createLinearGradient(0, y, 0, y + h);
  g2.addColorStop(0, "rgba(28,19,50,0.80)"); g2.addColorStop(1, "rgba(11,7,22,0.86)");
  ctx.fillStyle = g2; ctx.fill();
  ctx.lineWidth = 1.5; ctx.strokeStyle = "rgba(155,115,235,0.55)";
  ctx.shadowColor = "rgba(150,110,255,0.55)"; ctx.shadowBlur = 14; ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "rgba(205,175,255,0.16)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(x + c + 5, y + 2.5); ctx.lineTo(x + w - c - 5, y + 2.5); ctx.stroke();
  ctx.restore(); drawCount += 3;
}

function trophyIcon(cx, cy, s, color) {
  ctx.save(); ctx.fillStyle = color; ctx.strokeStyle = color; ctx.lineWidth = Math.max(1, s * 0.12);
  ctx.beginPath(); ctx.moveTo(cx - s * 0.5, cy - s * 0.55); ctx.lineTo(cx + s * 0.5, cy - s * 0.55);
  ctx.lineTo(cx + s * 0.3, cy + s * 0.12); ctx.lineTo(cx - s * 0.3, cy + s * 0.12); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.arc(cx - s * 0.5, cy - s * 0.32, s * 0.22, Math.PI * 0.5, Math.PI * 1.5); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx + s * 0.5, cy - s * 0.32, s * 0.22, -Math.PI * 0.5, Math.PI * 0.5); ctx.stroke();
  ctx.fillRect(cx - s * 0.09, cy + s * 0.12, s * 0.18, s * 0.26);
  ctx.fillRect(cx - s * 0.32, cy + s * 0.38, s * 0.64, s * 0.13);
  ctx.restore(); drawCount += 4;
}

function chargeBar(x, y, w, h, frac, now) {
  const r = h / 2;
  ctx.save();
  // orange ember under-glow — it's plasma, it glows
  ctx.globalCompositeOperation = "lighter";
  const ug = ctx.createRadialGradient(x + w / 2, y + h * 1.05, h * 0.3, x + w / 2, y + h * 1.05, w * 0.62);
  ug.addColorStop(0, "rgba(255,140,45,0.34)"); ug.addColorStop(0.6, "rgba(255,115,30,0.10)"); ug.addColorStop(1, "rgba(255,110,25,0)");
  ctx.fillStyle = ug; ctx.fillRect(x - w * 0.15, y - h * 0.2, w * 1.3, h * 2.6); drawCount++;
  ctx.globalCompositeOperation = "source-over";
  // track
  roundRectP(x, y, w, h, r);
  const tg = ctx.createLinearGradient(0, y, 0, y + h);
  tg.addColorStop(0, "rgba(9,6,20,0.96)"); tg.addColorStop(1, "rgba(26,19,46,0.96)");
  ctx.fillStyle = tg; ctx.fill();
  ctx.lineWidth = 1; ctx.strokeStyle = "rgba(125,95,185,0.55)"; ctx.stroke();
  // fill
  const fw = Math.max(frac > 0 ? h : 0, w * frac);
  if (fw > 0) {
    ctx.save(); roundRectP(x, y, w, h, r); ctx.clip();
    const fg = ctx.createLinearGradient(0, y, 0, y + h);
    fg.addColorStop(0, "#cda0ff"); fg.addColorStop(0.5, "#9a4dff"); fg.addColorStop(1, "#6a1fd0");
    ctx.fillStyle = fg; ctx.fillRect(x, y, fw, h); drawCount++;
    ctx.fillStyle = "rgba(255,255,255,0.22)"; ctx.fillRect(x, y + h * 0.12, fw, h * 0.30); drawCount++;
    ctx.globalCompositeOperation = "lighter";
    ctx.fillStyle = "rgba(225,190,255,0.95)"; ctx.fillRect(x + fw - 3, y, 3, h); drawCount++;
    ctx.restore();
  }
  ctx.restore();
}

function comboBar(rx, y, w, frac) {
  const h = 3, x = rx - w;
  ctx.save();
  roundRectP(x, y, w, h, h / 2); ctx.fillStyle = "rgba(70,52,22,0.85)"; ctx.fill();
  roundRectP(x, y, Math.max(h, w * frac), h, h / 2); ctx.fillStyle = "#ffce5a"; ctx.fill();
  ctx.restore(); drawCount += 2;
}

function renderHUD(now) {
  if (g.phase !== "run") return;
  const small = BASE();
  const x0 = 7, x1 = W - 7, y0 = 7;
  const ph = Math.min(Math.round(small * 0.185), 100);
  hudPanel(x0, y0, x1 - x0, ph);
  const rowL = y0 + ph * 0.30, rowN = y0 + ph * 0.57, rowS = y0 + ph * 0.83;
  const numSize = Math.min(small * 0.05, ph * 0.40);
  const labSize = Math.min(small * 0.020, ph * 0.155);
  const subSize = Math.min(small * 0.026, ph * 0.21);
  const LAB = "#a892dc", DIM = "#7d71a6", WHT = "#f2ecff", GOLD = "#ffce5a";

  // LEFT — score
  trophyIcon(x0 + ph * 0.20, rowL + ph * 0.02, ph * 0.16, LAB);
  const lx = x0 + ph * 0.38;
  text(STR.score, lx, rowL, labSize, LAB, "left", 700, 2);
  text(`${Math.floor(g.score)}`, lx, rowN, numSize, WHT, "left", 900);
  text(`${STR.best} ${Math.floor(Math.max(best, g.score))}`, lx, rowS, labSize, DIM, "left", 600, 1);

  // RIGHT — distance + combo
  const rx = x1 - ph * 0.20;
  text("DISTANCE", rx, rowL, labSize, LAB, "right", 700, 2);
  text(`${Math.floor(g.dist)}${STR.distance}`, rx, rowN, numSize, WHT, "right", 900);
  if (g.mult > 1) {
    text(`COMBO x${g.mult}`, rx, rowS, subSize * (comboMsgT > 0 ? 1.16 : 1), GOLD, "right", 900, 0.5);
    comboBar(rx, rowS + ph * 0.15, ph * 0.55, (g.combo % 5) / 5);
  } else {
    text("COMBO x1", rx, rowS, labSize, DIM, "right", 700, 1);
  }

  // CENTER — plasma charge
  const bx0 = Math.round(W * 0.32), bx1 = Math.round(W * 0.68), bw = bx1 - bx0;
  text(STR.goal, W / 2, rowL, labSize, LAB, "center", 700, 2.5);
  chargeBar(bx0, rowN - ph * 0.10, bw, ph * 0.20, Math.min(1, g.totalOrbs / CFG.orbGoal), now);
  text(`${Math.min(g.totalOrbs, CFG.orbGoal)}/${CFG.orbGoal}`, W / 2, rowS, subSize, "#cbb6ff", "center", 900);

  // overlays below the panel
  const below = y0 + ph;
  if (g.shield > 0) text(STR.shieldReady, W / 2, below + small * 0.030, small * 0.024, "#8de0ff", "center", 700, 1.5);
  if (runHintT > 0) {
    const hint = IS_TOUCH ? "\u25C0 \u25B6 lanes \u00B7 \u25B2 jump \u00B7 \u25BC slide" : "\u2190 \u2192 lanes \u00B7 \u2191 jump \u00B7 \u2193 slide";
    text(hint, W / 2, below + small * 0.065, Math.min(W * 0.032, small * 0.026), "#caa6ff", "center", 600, 1);
  }
  if (shieldMsgT > 0) text("SHIELD ACTIVATED", W / 2, H * 0.40, Math.min(W * 0.052, small * 0.042), "#8de0ff", "center", 900, 2);
  if (g.goalMet && Math.floor(now / 350) % 2 === 0)
    text(STR.gateAhead, W / 2, H * 0.28, Math.min(W * 0.044, small * 0.034), "#d9b3ff", "center", 700, 1);
}

// ============ fixed-timestep loop ============
const STEP = 1000 / 60;
let acc = 0, last = performance.now(), paused = false;
let frames = 0, fpsAt = last, fps = 0, maxDraws = 0;
addEventListener("blur", () => (paused = true));
addEventListener("focus", () => { paused = false; last = performance.now(); });
const QS = new URLSearchParams(location.search);
const dev = QS.has("dev");
if (QS.has("goal")) CFG.orbGoal = Math.max(1, parseInt(QS.get("goal"), 10) || CFG.orbGoal); // QA only
if (dev) document.getElementById("pr-dev").style.display = "block";

function consumeEdges() {
  // merge edges into a pending bucket so a keypress is never lost on a
  // frame that runs zero sim steps (120 Hz displays)
  const e = edges; edges = { left: false, right: false, jump: false, down: false };
  pending.left = pending.left || e.left;
  pending.right = pending.right || e.right;
  pending.jump = pending.jump || e.jump;
  pending.down = pending.down || e.down;
  pending.any = pending.any || anyKeyEdge;
  anyKeyEdge = false;
}
let pending = { left: false, right: false, jump: false, down: false, any: false };

function frame(now) {
  requestAnimationFrame(frame);
  if (paused) { last = now; return; }              // assets pop in as they decode; never block play
  padEdges();
  acc += (now - last) * (slowmo > 0 ? 0.45 : 1); last = now;
  if (acc > 250) acc = 250; // clamp after tab restore / hiccups
  if (slowmo > 0) slowmo -= STEP / 1000;
  if (shakeT > 0) shakeT = Math.max(0, shakeT - STEP / 1000 / 0.4);

  consumeEdges();
  while (acc >= STEP) {
    acc -= STEP;
    if (g.phase === "title" && pending.any) {
      start(g);
      pending = { left: false, right: false, jump: false, down: false, any: false }; // don't leak the start key into play
    } else if ((g.phase === "over" || g.phase === "win") && pending.any && now - overAt > 1400) {
      newRun();
      pending = { left: false, right: false, jump: false, down: false, any: false };
    }
    step(g, STEP / 1000, pending);
    pending = { left: false, right: false, jump: false, down: false, any: false };
    // event → presentation/audio (event table §9)
    if (g.justCollected) { sfx("orb"); collectPulse = 1; popAt(); }
    if (g.justCombo) { comboMsgT = 1.0; vibe(15); }
    if (g.justJumped) sfx("jump");
    if (g.justSlid) sfx("slide");
    if (g.justShield) { sfx("shield"); shieldMsgT = 1.4; vibe([0, 30, 40, 30]); }
    if (g.justGoal) sfx("shield");
    if (g.justWon) { sfx("win"); overAt = now; trySaveBest(); burst(); vibe([0, 40, 50, 40]); }
    if (g.justShieldBreak) { sfx("shieldbreak"); shieldShatter(); shakeT = Math.max(shakeT, 0.5); vibe([0, 25, 30, 25]); }
    if (g.justCrashed) { if (!g.justShieldBreak) sfx("crash"); shakeT = 1; vibe(60); if (g.phase === "over") { overAt = now; deaths++; trySaveBest(); slowmo = 0.5; } else slowmo = 0.4; }
  }
  // controls hint re-shows at the top for ~2s whenever a fresh run begins (incl. retry)
  if (g.phase === "run" && lastPhase !== "run") runHintT = 2.0;
  lastPhase = g.phase;
  if (runHintT > 0) runHintT -= 1 / 60;
  if (shieldMsgT > 0) shieldMsgT -= 1 / 60;
  if (comboMsgT > 0) comboMsgT -= 1 / 60;
  if (collectPulse > 0) collectPulse -= 1 / 12;
  stepParticles(1 / 60);
  syncUI();
  render(now);

  if (dev) {
    frames++; maxDraws = Math.max(maxDraws, drawCount);
    if (now - fpsAt >= 500) {
      fps = Math.round((frames * 1000) / (now - fpsAt)); frames = 0; fpsAt = now;
      document.getElementById("pr-dev").textContent =
        `${fps} fps\ndraws ${drawCount} (max ${maxDraws})\nents ${g.ents.length}\nspeed ${g.speed.toFixed(1)}`;
    }
  }
}
function trySaveBest() {
  try { if (g.score > best) localStorage.setItem("plasmaRunBest", String(Math.floor(g.score))); } catch (e) {}
}
requestAnimationFrame(frame);